import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import {
  Box, Heading, useToast, Spinner, Alert, AlertIcon, Input, Select, VStack, Button, Modal,
  ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton, useDisclosure, Flex
} from '@chakra-ui/react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { SearchIcon } from '@chakra-ui/icons';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const ModifyUser = () => {
  const [userInfos, setUserInfos] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const toast = useToast();
  const gridRef = useRef();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isConfirmationOpen, onOpen: onOpenConfirmation, onClose: onCloseConfirmation } = useDisclosure();
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [userInfoResponse, usersResponse] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/api/Users/userinfo`),
          axios.get(`${process.env.REACT_APP_API_URL}/api/Users/users`)
        ]);
        setUserInfos(userInfoResponse.data.$values || userInfoResponse.data);
      } catch (error) {
        setError('Error fetching data');
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const openModal = (userInfo, deactivating = false) => {
    setSelectedUser(userInfo);
    setIsDeactivating(deactivating);
    onOpen();
  };

  const closeModal = () => {
    setSelectedUser(null);
    onClose();
    setNewPassword('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSelectedUser((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    setNewPassword(e.target.value);
  };

  const handleSubmit = async () => {
    if (!selectedUser) return;

    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/Users/activate`, {
        refid: selectedUser.refid,
        email: selectedUser.email,
        role: selectedUser.role,
        password: newPassword
      });

      toast({
        title: "User activated successfully",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      setSelectedUser(null);
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/Users/userinfo`);
      setUserInfos(res.data.$values || res.data);
    } catch (error) {
      toast({
        title: "Error updating user",
        description: error.response?.data || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
      closeModal();
    }
  };

  const handleDeactivate = async () => {
    if (!selectedUser) return;

    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/Users/deactivate/${selectedUser.userId}`);
      toast({
        title: "User deactivated successfully",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      setSelectedUser(null);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/Users/userinfo`);
      setUserInfos(response.data.$values || response.data);
    } catch (error) {
      toast({
        title: "Error deactivating user",
        description: error.response?.data || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
      closeModal();
    }
  };

  const renderButtons = (params) => {
    const user = params.data.users?.$values?.[0];
    const isActive = user?.isActive;
    return (
      <Flex gap={2}>
        {user ? (
          isActive ? (
            <Button size="sm" colorScheme="red" onClick={() => openModal({ ...params.data, userId: user.userId }, true)}>
              Deactivate
            </Button>
          ) : (
            <Button size="sm" colorScheme="blue" onClick={() => openModal({ ...params.data, userId: user.userId })}>
              Activate
            </Button>
          )
        ) : (
          <Button size="sm" colorScheme="green" onClick={() => openModal(params.data)}>
            Add
          </Button>
        )}
      </Flex>
    );
  };

  const columnDefs = useMemo(() => [
    {
      headerName: "S. No.",
      valueGetter: "node.rowIndex + 1",
      filter: false,
      sortable: false,
      floatingFilter: false,
      resizable: false,
      tooltipValueGetter: (params) => `Row ${params.node.rowIndex + 1}`
    },
    { headerName: "Refid", field: "refid", filter: true, floatingFilter: true, resizable: true, tooltipField: "refid" },
    { headerName: "Name", field: "name", filter: true, floatingFilter: true, resizable: true, tooltipField: "name" },
    { headerName: "Empcode", field: "empcode", filter: true, floatingFilter: true, resizable: true, tooltipField: "Empcode" },
    { headerName: "Email", field: "email", filter: true, floatingFilter: true, resizable: true, tooltipField: "email" },
    { headerName: "City", field: "city", filter: true, floatingFilter: true, resizable: true, tooltipField: "city" },
    { headerName: "Designation", field: "designation", filter: true, floatingFilter: true, resizable: true, tooltipField: "designation" },
    // { headerName: "Department", field: "department", filter: true, floatingFilter: true, resizable: true, tooltipField: "department" },
    { headerName: "Office", field: "office", filter: true, floatingFilter: true, resizable: true, tooltipField: "office" },
    { headerName: "Mobile", field: "mobile", filter: true, floatingFilter: true, resizable: true, tooltipField: "mobile" },
    {
      headerName: "Actions",
      field: "actions",
      cellRenderer: renderButtons,
      resizable: true,
      tooltipField: "actions"
    },
  ], []);

  const handleConfirmationClose = () => {
    onCloseConfirmation();
  };

  const handleConfirmationOpen = (action) => {
    setConfirmationAction(() => action);
    onOpenConfirmation();
  };

  const [confirmationAction, setConfirmationAction] = useState(() => () => {});

  const onGridReady = (params) => {
    gridRef.current = params.api;
    params.api.sizeColumnsToFit();
  };

  const onBtExport = () => {
    if (!gridRef.current) return;
    const rowData = [];
    gridRef.current.forEachNode((node) => rowData.push(node.data));
    const worksheet = XLSX.utils.json_to_sheet(rowData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Marksheet');
    XLSX.writeFile(workbook, 'marksheet.xlsx');
  };

  const handleQuickFilter = (event) => {
    setSearchText(event.target.value);
    gridRef.current.setQuickFilter(event.target.value);
  };

  if (loading) {
    return (
      <Box className="flex justify-center items-center h-full">
        <Spinner size="xl" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="flex justify-center items-center h-full">
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box className="relative lg:w-[90%] md:w-full w-full mx-auto mt-10">
      <Heading size="lg" color="gray.800" fontWeight="medium" mb={6}>
        Modify User
      </Heading>
      <Flex mb={4} justifyContent="space-between" alignItems="center">
        <Input
          placeholder="Search..."
          value={searchText}
          onChange={handleQuickFilter}
          width="300px"
          variant="outline"
        />
        <Button onClick={onBtExport} className="bg-green-500 text-white px-4 py-2 rounded">Export to Excel</Button>
      </Flex>
      <div className="ag-theme-alpine" style={{ height: 600, width: "100%", overflowX: "auto", overflowY: "auto" }}>
        <AgGridReact
          ref={gridRef}
          rowData={Array.isArray(userInfos) ? userInfos : []}
          columnDefs={columnDefs}
          defaultColDef={{ flex: 1, minWidth: 100, filter: true, sortable: true, floatingFilter: true, resizable: true, tooltipComponent: 'agTooltipComponent' }}
          onGridReady={onGridReady}
          domLayout='autoHeight'
          quickFilterText={searchText}
          pagination={true}
          paginationPageSize={10}
          suppressHorizontalScroll={false}
        />
      </div>
      <Modal isOpen={isOpen} onClose={closeModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{isDeactivating ? 'Deactivate User' : 'Edit User'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <Input
                type="text"
                name="refid"
                value={selectedUser?.refid}
                isReadOnly
                placeholder="Refid"
              />
              <Input
                type="text"
                name="name"
                value={selectedUser?.name}
                isReadOnly
                placeholder="Name"
              />
              <Input
                type="text"
                name="email"
                value={selectedUser?.email}
                onChange={handleInputChange}
                placeholder="Email"
              />
              <Input
                type="text"
                name="city"
                value={selectedUser?.city}
                isReadOnly
                placeholder="City"
              />
              <Input
                type="text"
                name="designation"
                value={selectedUser?.designation}
                isReadOnly
                placeholder="Designation"
              />
              <Input
                type="text"
                name="department"
                value={selectedUser?.department}
                isReadOnly
                placeholder="Department"
              />
              {!isDeactivating && (
                <>
                  <Select
                    name="role"
                    value={selectedUser?.role}
                    onChange={handleInputChange}
                    placeholder="Select role"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </Select>
                  <Input
                    type="password"
                    name="password"
                    value={newPassword}
                    onChange={handlePasswordChange}
                    placeholder="New Password"
                  />
                </>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={() => handleConfirmationOpen(isDeactivating ? handleDeactivate : handleSubmit)}>
              {isDeactivating ? 'Deactivate User' : 'Activate User'}
            </Button>
            <Button variant="ghost" onClick={closeModal}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal isOpen={isConfirmationOpen} onClose={handleConfirmationClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{isDeactivating ? 'Deactivate User' : 'Activate User'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            Are you sure you want to {isDeactivating ? 'deactivate' : 'activate'} this user?
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="red" mr={3} onClick={handleConfirmationClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={() => { confirmationAction(); handleConfirmationClose(); }}>
              Confirm
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ModifyUser;
